import { Layout } from '@/components/Layout';
import { type Option, PerPageSelect } from '@/components/Form';
import { Button } from '@/components/Elements';
import { graphql, readFragment, useQuery } from '@/lib/graphql';
import { PRIVATE_TOP_PATH } from '@/routes/private';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { type MultiSelectFieldKey, type OrganizationSort } from '../types';
import { FilterSection } from '../components/FilterSection';
import { OrganizationTable } from '../components/OrganizationTable';
import { useOrganizationFilters } from '../hooks/useOrganizationFilters';

const DEFAULT_PER_PAGE = 20;

const ORGANIZATION_LIST_QUERY = graphql(
  `
    query OrganizationList(
      $condition: OrganizationSearchConditionInput
      $page: Int!
      $perPage: Int!
    ) {
      organizations(condition: $condition, page: $page, perPage: $perPage) {
        ...OrganizationTableFragment
      }
    }
  `,
  [OrganizationTable.fragments.collection]
);

export const OrganizationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);

  const {
    formState,
    appliedFilters,
    isFilterExpanded,
    condition,
    setQueryInput,
    setIsFilterExpanded,
    handleSelectChange: handleFilterSelectChange,
    handleSubmit: handleFilterSubmit,
    handleReset: handleFilterReset,
    handleSortChange: handleFilterSortChange,
  } = useOrganizationFilters();

  const { data, loading, error } = useQuery(ORGANIZATION_LIST_QUERY, {
    variables: {
      page,
      perPage,
      condition,
    },
    notifyOnNetworkStatusChange: true,
  });

  const isUnauthenticated = error?.graphQLErrors.some(
    (graphQLError) => graphQLError.extensions?.code === 'UNAUTHENTICATED'
  );

  const isInitialLoading = loading && !data;
  const isRefetching = loading && !!data;

  const organizationCollectionFragment = data?.organizations;
  const organizationCollection = organizationCollectionFragment
    ? readFragment(
        OrganizationTable.fragments.collection,
        organizationCollectionFragment
      )
    : undefined;
  const pageInfo = organizationCollection?.pageInfo;

  const handleSearch = (submittedQuery: string | undefined) => {
    handleFilterSubmit(submittedQuery);
    setPage(1);
  };

  const handleSelectChange =
    (field: MultiSelectFieldKey) => (options: readonly Option[] | null) => {
      handleFilterSelectChange(field)(options);
      setPage(1);
    };

  const handleSortChange = (sort: OrganizationSort) => {
    handleFilterSortChange(sort);
    setPage(1);
  };

  const handleResetFilters = () => {
    handleFilterReset();
    setPage(1);
  };

  const handlePageChange = (pageStart: number) => {
    const nextPage = Math.floor(pageStart / perPage) + 1;
    setPage(nextPage);
  };

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(PRIVATE_TOP_PATH);
    }
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <div className="mb-2">
          <Button
            color="transparent"
            size="sm"
            className="w-fit pl-0 text-gray-600 hover:text-gray-800"
            startIcon={<ArrowLeftIcon className="size-4" />}
            onClick={handleBackClick}
          >
            {t('戻る')}
          </Button>
          <h1 className="mt-1 text-xl font-semibold text-gray-900 sm:text-2xl">
            {t('情報源一覧')}
          </h1>
        </div>
        <hr className="mb-4" />

        <div className="flex w-full flex-col gap-y-2">
          <FilterSection
            formState={formState}
            appliedFilters={appliedFilters}
            isExpanded={isFilterExpanded}
            condition={condition}
            onToggle={() => setIsFilterExpanded(!isFilterExpanded)}
            onSearch={handleSearch}
            onQueryChange={setQueryInput}
            onSelectChange={handleSelectChange}
            onReset={handleResetFilters}
          />

          {pageInfo && (
            <div className="flex w-full flex-col gap-2 text-xs text-zinc-700 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {t('約 {{articleCount}} 件', {
                  articleCount: pageInfo.totalCount.toLocaleString(),
                })}
              </span>
              <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                <PerPageSelect
                  label={t('表示件数')}
                  value={perPage}
                  onChange={(value) => {
                    setPerPage(value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
          )}

          <OrganizationTable
            data={organizationCollectionFragment}
            isRefetching={isRefetching}
            isInitialLoading={isInitialLoading}
            error={error}
            isUnauthenticated={isUnauthenticated ?? false}
            onPageChange={handlePageChange}
            sort={formState.sort}
            onSortChange={handleSortChange}
          />
        </div>
      </div>
    </Layout>
  );
};
